import { Pagination, PaginationItem, PaginationProps } from "@mui/material";
import FirstIcon from "./images/first-icon.svg?react";
import PrevIcon from "./images/prev-icon.svg?react";
import NextIcon from "./images/next-icon.svg?react";
import LastIcon from "./images/last-icon.svg?react";

export type CorePaginationProps = Omit<
	PaginationProps,
	| "color"
	| "hideNextButton"
	| "hidePrevButton"
	| "renderItem"
	| "shape"
	| "showFirstButton"
	| "showLastButton"
	| "variant"
> & { paginationWrap?: boolean };

export const CorePagination = (props: CorePaginationProps) => {
	const { paginationWrap, ...paginationProps } = props;

	return (
		<Pagination
			{...paginationProps}
			showFirstButton
			showLastButton
			count={(props.count ?? 0) < 1 ? 1 : props.count}
			disabled={(props.count ?? 0) < 1 ? true : props.disabled}
			sx={{
				"& .MuiPagination-ul": {
					alignItems: "center",
					flexWrap: paginationWrap ? "wrap" : "nowrap",
					gap: "4px",
					// The root has overflowX: "auto" (theme/index.tsx), which per spec forces overflowY
					// to effectively become "auto" too, clipping the focus-visible box-shadow that paints
					// outside the item's border box. Padding here gives that shadow room so it isn't cut off.
					padding: "4px",
				},
				"& .MuiPaginationItem-root": {
					height: "28px",
					minWidth: "28px",
					margin: 0,
				},
				"& .MuiPaginationItem-page": {
					backgroundColor: "#f5f5f5",
					border: "1px solid transparent",
					borderRadius: "6px",
					color: "#575757",
					"&:hover": {
						backgroundColor: "#eeeeee",
						borderColor: "transparent",
					},
					"&:active": {
						backgroundColor: "#e5e5e5",
						borderColor: "transparent",
					},
					"&:focus-visible": {
						zIndex: 999,
					},
					"&.Mui-selected": {
						backgroundColor: "#ffffff",
						borderColor: "#d7d7d7",
						color: "#333333",
						"&:hover, &:focus-visible, &:active": {
							backgroundColor: "#ffffff",
						},
					},
					"&.Mui-disabled": {
						backgroundColor: "#f5f5f5",
						borderColor: "transparent",
					},
				},
				"& .MuiPaginationItem-firstLast, & .MuiPaginationItem-previousNext": {
					backgroundColor: "transparent",
					border: "none",
					color: "#575757",
					"&:hover, &:active": {
						backgroundColor: "transparent",
						border: "none",
					},
					"&:focus-visible": {
						backgroundColor: "transparent",
						border: "none",
						outline: "2px solid var(--focus-outline-color-dark)",
						outlineOffset: "2px",
						zIndex: 999,
					},
					"&.Mui-disabled": {
						backgroundColor: "transparent",
						border: "none",
						color: "#8b8b8b",
					},
				},
			}}
			renderItem={(item) => (
				<PaginationItem
					{...item}
					slots={{
						first: () => <FirstIcon />,
						previous: () => <PrevIcon />,
						next: () => <NextIcon />,
						last: () => <LastIcon />,
					}}
				/>
			)}
		/>
	);
};
